import { z } from 'zod'
import { HTTPError } from 'ky'
import { isValidPhoneNumber } from 'libphonenumber-js'
import type { AssetTag } from '@/feature/tags'

export const ALERT_FILTER_TOOLTIPS = {
  alertTitle:
    'Filter alerts by title. Type a keyword and press Enter to add it — alerts matching any keyword will be shown.',
  alertContacts:
    'Filter alerts by assigned contacts. Type a contact name and press Enter to add it.',
  duration:
    'Filter alerts by their minimum duration threshold in minutes. Enter a number and press Enter.',
  distance:
    'Filter alerts by their distance threshold in kilometres. Enter a number and press Enter.',
  alertEnabled: 'Filter alerts by their enabled status or whether they have contacts assigned.',
} as const

export const CORE_ALERT_TOOLTIPS = {
  setContacts:
    'Add up to 6 contacts - email addresses or WhatsApp numbers (e.g. +2721234567), separated by commas.',
} as const

/** Which vehicles a tag-capable alert fires for. */
export type AlertScope = 'allAssets' | 'assetsWithTags'

export const ALERT_SCOPE_OPTIONS: ReadonlyArray<{ value: AlertScope; label: string }> = [
  { value: 'allAssets', label: 'All Assets' },
  { value: 'assetsWithTags', label: 'Assets with Tags' },
]

/**
 * An alert carrying tags was scoped to them; anything else is a global alert.
 * Used to seed the scope toggle when an existing alert is opened for editing.
 */
export const resolveAlertScope = (tags?: AssetTag[] | null): AlertScope =>
  tags?.length ? 'assetsWithTags' : 'allAssets'

/**
 * Copy shown in place of the tag selector on alert types that aren't tied to a
 * vehicle match, where a tag could never fire the way the user expects.
 */
export const buildVehicleTagsUnsupportedMessage = (alertTitle: string): string =>
  `Vehicle tags can’t be added to “${alertTitle}”. Tags only apply to alerts that follow a ` +
  'specific vehicle — such as Arrival and Lateness alerts.'

export const emailSchema = z.string().email()
const phoneSchema = z.string().refine(isValidPhoneNumber, { message: 'Invalid phone number' })
export const contactEntrySchema = emailSchema.or(phoneSchema)

export type FormErrors = {
  name?: string
  contacts?: string
  fields?: string[]
  submit?: string
}

const API_FIELD_TO_FORM_FIELD: Record<string, keyof FormErrors> = {
  title: 'name',
  emails: 'contacts',
  phoneNumbers: 'contacts',
  templateName: 'submit',
  templateVariables: 'fields',
}

export const flattenErrorMessages = (value: unknown): string[] => {
  if (typeof value === 'string') return [value]
  if (Array.isArray(value)) return value.flatMap(flattenErrorMessages)
  if (typeof value === 'object' && value !== null) {
    return Object.entries(value).map(([key, val]) => {
      const label = key
        // because we convert snake to camel in requests...
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
      return `${label}: ${flattenErrorMessages(val).join(', ')}`
    })
  }
  return [String(value)]
}

export type ParsedApiErrors = {
  formErrors: FormErrors
  fieldErrorKeys: Set<string>
}

const camelToSnake = (str: string) => str.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)

export const extractFieldErrorKeys = (messages: unknown): Set<string> => {
  const keys = new Set<string>()
  if (Array.isArray(messages)) {
    for (const item of messages) {
      if (typeof item === 'object' && item !== null) {
        for (const key of Object.keys(item)) {
          keys.add(camelToSnake(key))
        }
      }
    }
  }
  return keys
}

export const parseApiErrors = async (error: unknown): Promise<ParsedApiErrors> => {
  const fallback: ParsedApiErrors = {
    formErrors: { submit: 'Failed to save alert. Please try again.' },
    fieldErrorKeys: new Set(),
  }

  if (!(error instanceof HTTPError)) return fallback

  try {
    const body = await error.response.json()
    const fieldErrors = body.errors ?? body
    const formErrors: FormErrors = {}
    let fieldErrorKeys = new Set<string>()

    for (const [apiField, messages] of Object.entries(fieldErrors)) {
      const formField = API_FIELD_TO_FORM_FIELD[apiField] ?? 'submit'
      const flattened = flattenErrorMessages(messages)

      if (formField === 'fields') {
        formErrors.fields = [...(formErrors.fields ?? []), ...flattened]
        fieldErrorKeys = extractFieldErrorKeys(messages)
      } else {
        const message = flattened.join(', ')
        formErrors[formField] = formErrors[formField]
          ? `${formErrors[formField]}; ${message}`
          : message
      }
    }

    return Object.keys(formErrors).length > 0 ? { formErrors, fieldErrorKeys } : fallback
  } catch {
    return fallback
  }
}
